import * as React from 'react';
import { AspectRatio } from 'react-aspect-ratio';
import { useNavigate } from 'react-router-dom';

import {
  Avatar,
  Box,
  Grid,
  ListItemAvatar,
  ListItemText,
  styled as MuiStyled,
  Stack,
  Typography,
} from '@mui/material';

import { isEmpty } from 'lodash';

import { Offer, OfferCategory, OfferRequirements, OfferStatus } from '../../../../graphql/API';
import { useListOfferQuery } from '../../../../hooks/reactQuery/useOfferQuery';
import useQueryParams from '../../../../hooks/useQueryParams';
import Button from '../../../Button/Button';
import { useCreateInvite } from '../context/CreateInvite.context';
import ListSkeleton from './ListSkeleton';
import { OfferNotFound } from './OfferNotFound';

const SelectedButton = MuiStyled(Button)({
  padding: '12px 48px',
});

type OfferListProps = {
  type: 'collab' | 'special event';
  data: Offer[];
  onClick: (value: string) => void;
};

const OfferList: React.FC<OfferListProps> = ({ type, data, onClick }) => {
  const handleOnClick = React.useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      onClick(e.currentTarget.value);
    },
    [onClick],
  );

  return data.length ? (
    <Box>
      <Grid container sx={{ mt: 1 }}>
        {data.map((x) => (
          <Grid item xs={6} key={x.id}>
            <Box
              sx={{
                borderRadius: '14px',
                border: '1px solid #EFEEEE',
                background: '#FFF',
                p: '12px 9px',
                m: '4px',
              }}
            >
              <ListItemAvatar sx={{ position: 'relative' }}>
                <AspectRatio ratio='16/9'>
                  <Avatar
                    sx={(theme) => ({ borderRadius: theme.spacing(1) })}
                    variant='rounded'
                    src={String(x.picture?.medium)}
                  />
                </AspectRatio>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography
                    fontSize='15px'
                    fontWeight='500'
                    marginBottom='59px'
                    marginTop={'17px'}
                    color='#232222'
                  >
                    {x.title}
                  </Typography>
                }
                secondary={
                  <Box
                    display={'flex'}
                    flexDirection={'column'}
                    gap={type !== 'collab' ? '35px' : '5px'}
                    alignItems={'flex-start'}
                  >
                    <SelectedButton
                      id={`btn-offer-${x.id}`}
                      variant='contained'
                      value={x.id}
                      onClick={handleOnClick}
                      fullWidth
                    >
                      Select
                    </SelectedButton>
                  </Box>
                }
              />
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  ) : null;
};

const Step1: React.FC = () => {
  const navigate = useNavigate();
  const { getQueryParam } = useQueryParams();
  const collabId = getQueryParam('collabId');

  const { data, isLoading } = useListOfferQuery({
    status: OfferStatus.active,
    category: OfferCategory.product,
  });
  const { dispatch } = useCreateInvite();

  const handleSubmit = React.useCallback(
    (value: string) => {
      const found = data.find((x) => x.id === value);

      if (found) {
        dispatch({
          type: 'next',
          payload: {
            offer: found,
            requirements: OfferRequirements.postOnInstagramStories,
          },
        });
      }
    },
    [data, dispatch],
  );

  React.useEffect(() => {
    if (!isLoading) {
      dispatch({ type: 'update', payload: { isEmptyOffer: !data.length } });
    }
  }, [isLoading, data, dispatch]);

  const createOfferHandle = React.useCallback(() => {
    navigate('/products/create');
  }, [navigate]);

  const productOffers =
    collabId && data ? data.filter((offer) => offer.id == collabId) : data || [];

  return (
    <Box
      mt={2}
      sx={{
        maxHeight: 'calc(600px - 100px)',
        overflowY: 'auto',
      }}
    >
      <Stack spacing={3}>
        {isLoading ? (
          <ListSkeleton />
        ) : (
          <>
            {!isEmpty(productOffers) && productOffers && productOffers?.length > 0 ? (
              <>
                <OfferList type='collab' data={productOffers} onClick={handleSubmit} />
              </>
            ) : (
              <OfferNotFound onCreateOffer={createOfferHandle} />
            )}
          </>
        )}
      </Stack>
    </Box>
  );
};

export default Step1;
